import serverClient, { browserClient } from './apolloClient';
import { cache } from 'react';
import { fetchListingData } from '@/helpers/list-page/list-page-helpers';

import {
  GET_ALL_OCCASIONS,
  GET_ALL_OFFERS,
  GET_EVENT_DETAIL_PAGE_BY_SLUG,
  GET_LPO_DETAIL_PAGE_BY_SLUG,
  GET_NEWS_DETAIL_PAGE_BY_SLUG,
  GET_OFFER_DETAIL_PAGE_BY_SLUG,
  GET_TENANT_ENTERTAINMENT_DETAIL_PAGE_BY_SLUG,
  GET_TENANT_FOOD_BEVERAGE_DETAIL_PAGE_BY_SLUG,
  GET_TENANT_SERVICE_DETAIL_PAGE_BY_SLUG,
  GET_TENANT_SERVICE_TYPE_DETAIL_PAGE_BY_SLUG,
  GET_TENANT_SHOP_DETAIL_PAGE_BY_SLUG,
} from './queries/getDetailPageBySlug';
import { GET_URL_SUB_DIRECTORY } from './queries/getUrlSubDirectory';
import { MEETING_PLACE_PAGE_Occasion_QUERY } from './queries/meetingPlacePageOccasionListQuery';
import { DateTime } from 'luxon';
import { DEFAULT_LOCALE } from '@/config/app.config';
import { unstable_cache } from 'next/cache';
import { isBrowser } from '@/helpers/is-browser';

// In the browser, route through the same-origin /api/graphql proxy so the
// Contentful token is never exposed. On the server, call Contentful directly.
const client = isBrowser ? browserClient : serverClient;

const CONTENT_TYPE_MAP: Record<string, { query: any; collectionKey: string }> =
  {
    ContentfulMeetingPlacePageOfferItem: {
      query: GET_OFFER_DETAIL_PAGE_BY_SLUG,
      collectionKey: 'meetingPlacePageOfferCollection',
    },
    ContentfulMeetingPlacePageNewsItem: {
      query: GET_NEWS_DETAIL_PAGE_BY_SLUG,
      collectionKey: 'meetingPlacePageNewsItemCollection',
    },
    ContentfulMeetingPlacePageEventItem: {
      query: GET_EVENT_DETAIL_PAGE_BY_SLUG,
      collectionKey: 'meetingPlacePageEventCollection',
    },
    ContentfulMeetingPlacePageLeisurePackageOfferItem: {
      query: GET_LPO_DETAIL_PAGE_BY_SLUG,
      collectionKey: 'meetingPlacePageLeisurePackageOfferItemCollection',
    },
    ContentfulMeetingPlacePageTenantShopItem: {
      query: GET_TENANT_SHOP_DETAIL_PAGE_BY_SLUG,
      collectionKey: 'meetingPlacePageTenantShopCollection',
    },
    ContentfulMeetingPlacePageTenantFoodBevItem: {
      query: GET_TENANT_FOOD_BEVERAGE_DETAIL_PAGE_BY_SLUG,
      collectionKey: 'meetingPlacePageTenantFoodBeverageCollection',
    },
    ContentfulMeetingPlacePageTenantEntertainmentItem: {
      query: GET_TENANT_ENTERTAINMENT_DETAIL_PAGE_BY_SLUG,
      collectionKey: 'meetingPlacePageTenantEntertainmentCollection',
    },
    ContentfulMeetingPlacePageServiceItem: {
      query: GET_TENANT_SERVICE_DETAIL_PAGE_BY_SLUG,
      collectionKey: 'meetingPlacePageServiceCollection',
    },
    ContentfulLibraryServiceType: {
      query: GET_TENANT_SERVICE_TYPE_DETAIL_PAGE_BY_SLUG,
      collectionKey: 'libraryServiceTypeCollection',
    },
  };

const isTenantCollection = (collectionKey: string) => {
  return (
    collectionKey === 'meetingPlacePageTenantShopCollection' ||
    collectionKey === 'meetingPlacePageTenantEntertainmentCollection' ||
    collectionKey === 'meetingPlacePageTenantFoodBeverageCollection'
  );
};

// export async function resolveDetailPageBySlug({
async function resolveDetailPageBySlugUncached({
  parent,
  slug,
  locale,
  tagId,
  config,
  preview = false,
}: {
  parent: string;
  slug: string;
  locale: string;
  tagId: string;
  config: any;
  preview?: boolean;
}) {
  const FALLBACK_LOCALE = DEFAULT_LOCALE;

  const { data: urlData }: any = await client.query({
    query: GET_URL_SUB_DIRECTORY,
    variables: {
      locale,
    },
  });

  const urlSubDirectoryCollection =
    urlData?.libraryUrlSubdirectoryCollection?.items;

  if (!urlSubDirectoryCollection || urlSubDirectoryCollection.length < 0) {
    console.warn('No content type found in urlSubDirectory for slug:', slug);
    return null;
  }

  let contentType = urlSubDirectoryCollection?.find(
    (item: any) => item?.urlSubdirectory == parent,
  )?.contentTypes[0];
  if (
    contentType === 'ContentfulMeetingPlacePageServiceItem' &&
    config?.NEXT_PUBLIC_SHOW_NEW_SERVICE_DESIGN === 'true'
  ) {
    contentType = 'ContentfulLibraryServiceType';
  }
  const queryConfig = CONTENT_TYPE_MAP[contentType];

  if (!queryConfig) {
    console.error('Unknown content type:', contentType);
    return null;
  }

  const { data }: any = await client.query({
    query: queryConfig.query,
    variables: {
      slug,
      locale,
      tagId: tagId,
    },
    fetchPolicy: 'no-cache',
  });

  let items = data?.[queryConfig.collectionKey]?.items || [];
  if (isTenantCollection(queryConfig.collectionKey)) {
    items = (data?.bySlug?.items?.length > 0 && data?.bySlug?.items) || [];
  }
  let found = items[0] || null;

  //  Try fallback locale (English)
  if (!found && locale !== FALLBACK_LOCALE) {
    const { data: en_locale_data }: any = await client.query({
      query: queryConfig.query,
      variables: {
        slug,
        tagId: tagId,
        locale: FALLBACK_LOCALE,
      },
      fetchPolicy: 'no-cache',
    });
    let items = en_locale_data?.[queryConfig.collectionKey]?.items || [];
    if (isTenantCollection(queryConfig.collectionKey)) {
      items =
        (en_locale_data?.bySlug?.items?.length > 0 &&
          en_locale_data?.bySlug?.items) ||
        [];
    }
    found = items[0] || null;
  }

  if (!found) return null;

  const type: string = found.__typename;

  async function fetchAllOccasions(tagId: string) {
    const limit = 50;
    let skip = 0;
    let allOccasions: any[] = [];
    let hasMore = true;

    while (hasMore) {
      const result: any = await client.query({
        query: GET_ALL_OCCASIONS,
        variables: {
          tagId: tagId,
          limit: limit,
          skip: skip,
        },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      });

      const occasionsCollection =
        result?.data?.meetingPlacePageOccasionsCollection?.items[0]
          ?.occasionsCollection;
      const items = occasionsCollection?.items?.filter(Boolean) || [];
      const total = occasionsCollection?.total || 0;

      allOccasions = [...allOccasions, ...items];

      skip += limit;
      hasMore = skip < total;
    }

    return allOccasions;
  }

  let occasionId: string | null = null;
  let offerPagesIds: Array<string> | null = [];

  if (contentType == 'ContentfulMeetingPlacePageOfferItem') {
    try {
      const { data }: any = await client.query({
        query: MEETING_PLACE_PAGE_Occasion_QUERY,
        variables: {
          locale,
          tagId: tagId,
        },
        fetchPolicy: 'no-cache',
      });

      occasionId =
        data?.meetingPlacePageOccasionsCollection?.items?.filter(Boolean)?.[0]
          ?.sys?.id;
    } catch (error) {
      console.warn('Error Fetching Occation: ', error);
    }

    const [allOffersData, allOccasions] = await Promise.all([
      client.query({
        query: GET_ALL_OFFERS,
        variables: {
          tagId: tagId,
        },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      }),
      fetchAllOccasions(tagId),
    ]);

    const allOffers = (
      allOffersData?.data as any
    )?.meetingPlacePageOfferCollection?.items?.filter(Boolean);
    // const allOccasions = (allOccasionsData as any)?.meetingPlacePageOccasionsCollection?.items[0]?.occasionsCollection?.items;

    const filterOffersByTenant = allOffers
      ?.filter(Boolean)
      ?.filter((t: any) => t.tenant?.sys?.id === found?.tenant?.sys?.id);

    const filterOccasionsByTenant = allOccasions
      ?.filter(Boolean)
      ?.filter((item: any) => {
        if (Object.keys(item).length === 0) {
          return false;
        }

        if (!item.validTo || !DateTime.fromISO(item.validTo).isValid) {
          return false;
        }

        if (item.tenant?.sys.id != found?.tenant?.sys?.id) {
          return false;
        }

        return DateTime.fromISO(item.validTo).diffNow('days').days > 0;
      });

    const offerPages = filterOccasionsByTenant
      ? [...filterOccasionsByTenant]
      : [];

    filterOffersByTenant?.forEach((page: any) => {
      const exists = offerPages.some(
        (item: any) => item?.sys?.id === page?.sys?.id,
      );
      if (!exists) {
        offerPages.push({
          sys: { id: page?.sys?.id },
          validTo: page?.validTo,
          tenant: { id: found?.tenant?.sys?.id },
        });
      }
    });

    offerPagesIds = offerPages
      .filter(
        (offer: any) =>
          DateTime.fromISO(offer?.validTo).diffNow('day').days > 0,
      )
      .map((offer) => offer?.sys?.id);
  }

  return { type, data: found, offerPagesIds };
}

// Cached version for production
//
// Request-scoped dedup: `generateMetadata` and the page render both call this
// resolver for the same detail request, but they resolve the tenant domain
// differently (x-tenant-domain vs Host), so `config` object references differ.
// React `cache()` gives us a per-request holder keyed only on the stable
// primitive tuple (plus the single config flag that can change the result), so
// the underlying Contentful resolution runs once per request. Preview is never
// cached.
const getResolutionHolder = cache(
  (
    _parent: string,
    _slug: string,
    _locale: string,
    _tagId: string,
    _newServiceDesign: boolean,
  ): { promise?: ReturnType<typeof resolveDetailPageBySlugUncached> } => ({}),
);

export async function resolveDetailPageBySlug({
  parent,
  slug,
  locale,
  tagId,
  config,
  preview = false,
}: {
  parent: string;
  slug: string;
  locale: string;
  tagId: string;
  config: any;
  preview?: boolean;
}) {
  // Don't cache in preview mode
  if (preview) {
    return resolveDetailPageBySlugUncached({
      parent,
      slug,
      locale,
      tagId,
      config,
      preview,
    });
  }

  const holder = getResolutionHolder(
    parent,
    slug,
    locale,
    tagId,
    config?.NEXT_PUBLIC_SHOW_NEW_SERVICE_DESIGN === 'true',
  );
  if (!holder.promise) {
    holder.promise = resolveDetailPageBySlugUncached({
      parent,
      slug,
      locale,
      tagId,
      config,
      preview,
    });
  }
  return holder.promise;
}
